import React from "react";
import { Target, Eye, Award, Users, Shield } from "lucide-react";
import { COMPANY, STATS } from "../lib/siteData";
import CTABanner from "../components/CTABanner";
import SEO from "../components/SEO";

const TEAM = [
  { name: "Ashwani Kumar Mishra", role: "Managing Director", initial: "A" },
  { name: "Suraj Kumar", role: "Director — Operations", initial: "S" },
  { name: "Priya Kumari", role: "Chief Solar Engineer", initial: "P" },
  { name: "Kundan Kumar", role: "Head of Projects", initial: "K" },
  { name: "Priti Singh", role: "HR Manager", initial: "P" },
];

export default function AboutPage() {
  return (
    <div data-testid="about-page">
      <SEO title="About Us" description="CNGTPL Solar is a Delhi-based solar EPC company with 10+ years of experience, 50+ MW installations and 12,000+ happy customers." />
      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-900 to-blue-900 text-white py-16 lg:py-24">
        <div className="container-narrow">
          <div className="badge-eco bg-blue-600/20 border-blue-600/30 text-blue-300 mb-4">About {COMPANY.name}</div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold tracking-tight mb-4 max-w-3xl">
            Engineering a <span className="text-yellow-400">brighter</span>, cleaner tomorrow
          </h1>
          <p className="text-lg text-slate-300 max-w-2xl">
            Since {COMPANY.founded}, CNGTPL Solar has helped over 12,000 homes and 200+ businesses across India switch to solar — installing over 50 MW of clean energy capacity.
          </p>
        </div>
      </section>

      {/* Intro */}
      <section className="section-pad">
        <div className="container-narrow grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="badge-eco mb-3">Our Story</div>
            <h2 className="text-3xl sm:text-4xl font-heading font-semibold text-slate-900 mb-5">Built on trust. Powered by engineering.</h2>
            <div className="space-y-4 text-slate-600 leading-relaxed">
              <p>
                We started in 2021 with a single mission: make solar simple for Indian homes and businesses. A decade later, we remain one of Delhi NCR's most trusted solar EPC companies — with a full in-house design, procurement and installation team.
              </p>
              <p>
                Every project we execute uses Tier-1 modules, MNRE-certified inverters and is backed by our 25-year performance guarantee. No outsourcing. No shortcuts.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4 mt-8">
              {STATS.slice(0, 4).map((s) => (
                <div key={s.label} className="border-l-4 border-blue-600 pl-4">
                  <div className="stat-number text-3xl font-bold text-slate-900">{s.value}</div>
                  <div className="text-xs uppercase tracking-widest text-slate-500">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-2xl overflow-hidden aspect-[4/5]">
            <img src="https://static.prod-images.emergentagent.com/jobs/af236e4b-6d37-4c12-98e2-7d0d973c0359/images/e11ec766b0c2196d728fbdc6a9098e037c63d18b1de8a97f1fbcef14fa01e261.png" className="w-full h-full object-cover" alt="CNGTPL Solar team" />
          </div>
        </div>
      </section>

      {/* Vision / Mission */}
      <section id="vision" className="section-pad bg-slate-50">
        <div className="container-narrow grid md:grid-cols-2 gap-6">
          {[
            { icon: Eye, title: "Our Vision", text: "To make clean, solar-powered energy the default choice for every Indian home and business by 2035.", accent: "emerald" },
            { icon: Target, title: "Our Mission", text: "Deliver dependable, affordable solar installations with transparent pricing, tier-1 components and lifetime customer support.", accent: "slate" },
          ].map((b) => (
            <div key={b.title} className={`rounded-2xl p-8 ${b.accent === "emerald" ? "bg-blue-600 text-white" : "bg-slate-900 text-white"}`}>
              <b.icon className="w-10 h-10 mb-5" />
              <h3 className="font-heading text-2xl font-semibold mb-3">{b.title}</h3>
              <p className="text-base leading-relaxed opacity-90">{b.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Certifications */}
      <section className="section-pad">
        <div className="container-narrow">
          <div className="text-center mb-10">
            <div className="badge-eco mb-3 mx-auto">Certifications</div>
            <h2 className="text-3xl sm:text-4xl font-heading font-semibold text-slate-900">Certified, approved & trusted</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: "MNRE Approved", icon: Shield },
              { label: "ISO 9001:2015", icon: Award },
              { label: "BIS Certified Products", icon: Award },
              { label: "CEA Compliant", icon: Shield },
            ].map((c) => (
              <div key={c.label} className="rounded-xl border border-slate-200 p-6 text-center bg-white hover:border-blue-400 transition">
                <c.icon className="w-10 h-10 text-blue-600 mx-auto mb-3" />
                <div className="font-heading font-semibold text-slate-900">{c.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership */}
      <section id="leadership" className="section-pad bg-slate-50">
        <div className="container-narrow">
          <div className="text-center mb-10">
            <div className="badge-eco mb-3 mx-auto">Leadership</div>
            <h2 className="text-3xl sm:text-4xl font-heading font-semibold text-slate-900">Meet our leadership team</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {TEAM.map((t) => (
              <div key={t.name} className="bg-white rounded-2xl p-6 text-center border border-slate-200 hover:shadow-lg transition">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 text-white font-heading text-3xl font-bold mx-auto mb-4 flex items-center justify-center">
                  {t.initial}
                </div>
                <div className="font-heading text-lg font-semibold text-slate-900">{t.name}</div>
                <div className="text-sm text-blue-700 font-medium">{t.role}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTABanner />
    </div>
  );
}
